// Modal builder for a column's ID SERIES — the `series` block behind
// `auto_generate_prefixed()`. The user assembles the prefix from ordered parts
// (a form column or a slice of it, fixed text, a date part, a master-table
// lookup), sets the counter width, and watches a LIVE preview of the ids the
// rules will actually mint.
//
// The point of the preview is that this is otherwise hard to picture: the
// counter runs independently per distinct prefix, so the same rules give
// IND0000001 and USA0000001 at the same time. Showing two prefixes side by
// side makes that obvious without reading the docs.
//
// Mirrors LookupEditor / DisplayFormatEditor: local draft state, nothing is
// written to the model until Save.

import { useEffect, useMemo, useRef, useState } from 'react'

import type {
  ColumnDef, SeriesConfig, SeriesDateFormat, SeriesPart, SeriesPartSource,
} from '@twasta/page-runtime/txn'
import Modal from '@/components/common/Modal'
import { getTables, getTableInfo } from '@/api/designers'
import { livePreviewTables, livePreviewColumns } from '@/livepreview/api'

interface Props {
  open: boolean
  onClose: () => void
  column: ColumnDef | null
  /** Sibling column names on the same form (excluding the id itself). */
  formColumns: string[]
  project: string
  onSave: (series: SeriesConfig) => void
}

const SOURCE_LABELS: Record<SeriesPartSource, string> = {
  form: 'Form column',
  const: 'Fixed text',
  date: 'Date',
  lookup: 'Table lookup',
}

const SOURCE_HINTS: Record<SeriesPartSource, string> = {
  form: 'Part of the id comes from what the user typed in another field.',
  const: 'A literal that never changes — a company or document prefix.',
  date: 'The date the record is created. The counter restarts each period.',
  lookup: 'A value read from a master table that isn’t on this form.',
}

const DATE_FORMATS: Array<{ value: SeriesDateFormat; label: string }> = [
  { value: 'YYYY', label: 'YYYY — 4-digit year (2026)' },
  { value: 'YY', label: 'YY — 2-digit year (26)' },
  { value: 'YYYYMM', label: 'YYYYMM — year + month (202608)' },
  { value: 'YYMM', label: 'YYMM — short year + month (2608)' },
  { value: 'MM', label: 'MM — month (08)' },
  { value: 'DD', label: 'DD — day (02)' },
]

// Stand-in values for the preview, chosen so two different prefixes are
// visible at once — that is what shows the per-prefix counter.
const SAMPLE_VALUES = ['India', 'United States']
const SAMPLE_LOOKUP = ['AS', 'NA']

const overlayLbl: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)',
  display: 'block', marginBottom: 4,
}
const input: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 14, boxSizing: 'border-box',
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const smallBtn: React.CSSProperties = {
  padding: '4px 10px', fontSize: 13, borderRadius: 5, cursor: 'pointer',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)', color: 'var(--color-text)',
}
const iconBtn: React.CSSProperties = {
  ...smallBtn, padding: '2px 7px', lineHeight: 1.4,
}

function newPart(source: SeriesPartSource, firstColumn?: string): SeriesPart {
  if (source === 'const') return { source: 'const', text: '-' }
  if (source === 'date') return { source: 'date', format: 'YYYY' }
  if (source === 'lookup') {
    return {
      source: 'lookup', table: '', match_column: '', value_column: '',
      match_form_column: firstColumn || '', transform: 'upper',
    }
  }
  return {
    source: 'form', column: firstColumn || '', start: 1, length: 3,
    transform: 'upper',
  }
}

/** Apply a part's 1-based start / length / transform — the same slice the
 *  engine performs, so the preview cannot drift from the real value. */
function sliceAndTransform(raw: string, part: SeriesPart): string {
  const start = Math.max(1, Number(part.start) || 1)
  let seg = raw.slice(start - 1)
  const len = Number(part.length) || 0
  if (len > 0) seg = seg.slice(0, len)
  if (part.transform === 'upper') seg = seg.toUpperCase()
  else if (part.transform === 'lower') seg = seg.toLowerCase()
  return seg
}

function formatSampleDate(fmt: SeriesDateFormat | undefined): string {
  const d = new Date()
  const yyyy = String(d.getFullYear())
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  switch (fmt) {
    case 'YY': return yyyy.slice(2)
    case 'MM': return mm
    case 'DD': return dd
    case 'YYYYMM': return yyyy + mm
    case 'YYMM': return yyyy.slice(2) + mm
    default: return yyyy
  }
}

/** One sample id for the Nth stand-in record. */
function previewId(series: SeriesConfig, variant: number, counter: number): string {
  const parts = series.parts || []
  let prefix = ''
  for (const p of parts) {
    const source = p.source || 'form'
    if (source === 'const') prefix += p.text || ''
    else if (source === 'date') prefix += formatSampleDate(p.format)
    else if (source === 'lookup') {
      prefix += sliceAndTransform(SAMPLE_LOOKUP[variant] || 'XX', p)
    } else {
      prefix += sliceAndTransform(SAMPLE_VALUES[variant] || 'SAMPLE', p)
    }
  }
  const width = Math.max(1, Number(series.width) || 4)
  return prefix + (series.separator || '') + String(counter).padStart(width, '0')
}

/** The one-line description shown on the Property Panel's Series row. */
export function seriesSummary(col: ColumnDef | null | undefined): string {
  const series = col?.series
  const parts = series?.parts
  if (!Array.isArray(parts) || parts.length === 0) return ''
  const bits = parts.map((p) => {
    const source = p.source || 'form'
    if (source === 'const') return `"${p.text || ''}"`
    if (source === 'date') return p.format || 'YYYY'
    const base = source === 'lookup'
      ? `${p.value_column || '?'} from ${p.table || '?'}`
      : (p.column || '?')
    const start = Number(p.start) || 1
    const len = Number(p.length) || 0
    const slice = len > 0
      ? (start > 1 ? ` [${start}–${start + len - 1}]` : ` [${len}]`)
      : ''
    return base + slice
  })
  const width = Math.max(1, Number(series?.width) || 4)
  const sep = series?.separator ? ` + "${series.separator}"` : ''
  return `${bits.join(' + ')}${sep} + ${width} digits`
}

export default function SeriesEditor({
  open, onClose, column, formColumns, project, onSave,
}: Props) {
  const [parts, setParts] = useState<SeriesPart[]>([])
  const [width, setWidth] = useState(7)
  const [separator, setSeparator] = useState('')
  const [tables, setTables] = useState<string[]>([])
  const [colsByTable, setColsByTable] = useState<Record<string, string[]>>({})
  // Read inside the seeding effect without making it depend on the (unstable)
  // formColumns array.
  const firstColumnRef = useRef(formColumns[0])
  firstColumnRef.current = formColumns[0]

  // Seed the draft from the saved model when the dialog opens or the column
  // changes — deliberately NOT on `formColumns`, which the parent rebuilds as a
  // fresh array on every render: depending on it would reset the user's
  // in-progress edits the moment anything upstream re-rendered.
  useEffect(() => {
    if (!open) return
    const s = column?.series
    const existing = Array.isArray(s?.parts) ? s!.parts! : []
    setParts(existing.length
      ? existing.map((p) => ({ ...p }))
      // A column that just picked the token starts with the common case:
      // the first field on the form, first three characters, upper-cased.
      : [newPart('form', firstColumnRef.current)])
    setWidth(Math.max(1, Number(s?.width) || 7))
    setSeparator(s?.separator || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, column])

  // Master tables for the lookup part — the deployed preview schema plus the
  // Database-Designer catalog, exactly like LookupEditor.
  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    Promise.allSettled([livePreviewTables(project), getTables(project)])
      .then(([preview, design]) => {
        if (cancelled) return
        const seen = new Map<string, string>()
        const add = (n?: string) => {
          const name = (n || '').trim()
          if (name && !seen.has(name.toLowerCase())) {
            seen.set(name.toLowerCase(), name)
          }
        }
        if (preview.status === 'fulfilled') {
          for (const t of preview.value?.tables || []) add(t.name)
        }
        if (design.status === 'fulfilled') {
          const list = Array.isArray(design.value?.tables) ? design.value.tables : []
          for (const t of list) add(t.name)
        }
        setTables([...seen.values()].sort((a, b) => a.localeCompare(b)))
      })
      .catch(() => { if (!cancelled) setTables([]) })
    return () => { cancelled = true }
  }, [open, project])

  // Columns of every table referenced by a lookup part, fetched once each.
  useEffect(() => {
    if (!open || !project) return
    const wanted = [...new Set(
      parts.filter((p) => p.source === 'lookup' && (p.table || '').trim())
        .map((p) => (p.table || '').trim()),
    )].filter((t) => !(t in colsByTable))
    if (!wanted.length) return
    let cancelled = false
    // Debounced: `parts` changes on every keystroke in the table box, and
    // without this each partial name ("C", "CO", "COU"…) would fire its own
    // catalog fetch.
    const timer = window.setTimeout(() => {
    Promise.all(wanted.map(async (t) => {
      const results = await Promise.allSettled([
        livePreviewColumns(project, t), getTableInfo(project, t),
      ])
      const names = new Set<string>()
      const [live, design] = results
      if (live.status === 'fulfilled') {
        for (const c of live.value?.columns || []) {
          if (c?.column_name) names.add(c.column_name)
        }
      }
      if (design.status === 'fulfilled') {
        const list = Array.isArray(design.value?.columns) ? design.value.columns : []
        for (const c of list) if (c?.name) names.add(c.name)
      }
      return [t, [...names].sort((a, b) => a.localeCompare(b))] as const
    })).then((pairs) => {
      if (cancelled) return
      setColsByTable((prev) => {
        const next = { ...prev }
        for (const [t, cols] of pairs) next[t] = cols
        return next
      })
    }).catch(() => { /* leave the pickers as free text */ })
    }, 350)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [open, project, parts, colsByTable])

  const draft = useMemo<SeriesConfig>(
    () => ({ parts, width, separator }), [parts, width, separator],
  )

  // Does the id vary per record's data, or is it one declared series?
  const hasDataPart = useMemo(
    () => parts.some((p) => p.source === 'form' || p.source === 'lookup'),
    [parts],
  )

  // How many characters the id can reach vs how many the column holds. The
  // engine never truncates a counter, so an undersized column fails at SAVE —
  // long after the rules were written. Showing the budget while they are being
  // written is the point at which it is cheap to fix.
  const budget = useMemo(() => {
    let low = 0
    let high = 0
    let estimated = false
    for (const p of parts) {
      const src = p.source || 'form'
      if (src === 'const') {
        const n = (p.text || '').length
        low += n; high += n
      } else if (src === 'date') {
        const n = (p.format || 'YYYY').length
        low += n; high += n
      } else {
        const len = Number(p.length) || 0
        if (len > 0) { low += len; high += len } else {
          // Open-ended: the whole source value can land in the prefix.
          estimated = true
          high += src === 'lookup' ? 10 : 40
        }
      }
    }
    const tail = (separator || '').length + Math.max(1, width)
    return { low: low + tail, high: high + tail, estimated }
  }, [parts, separator, width])

  const colSize = Number(column?.db_size) || 0
  const colType = (column?.col_type || 'VARCHAR').toUpperCase()
  const overflows = colSize > 0 && budget.high > colSize
  const certainOverflow = colSize > 0 && budget.low > colSize

  // Blocking problems only — the lint covers the softer advice (mandatory
  // sources, db_size) where it can see the whole model.
  //
  // Note there is deliberately NO "must include a form column" rule: a prefix
  // of pure fixed text is a legitimate, common design (ART-00001, ART-00002,
  // …). One counter is the CORRECT behaviour there, and unlike auto_generate()
  // the prefix is declared, so the first row is ART-00001 with no seed row and
  // the width never drifts.
  const problems = useMemo(() => {
    const out: string[] = []
    if (!parts.length) out.push('Add at least one part.')
    parts.forEach((p, i) => {
      const n = i + 1
      if (p.source === 'form' && !(p.column || '').trim()) {
        out.push(`Part ${n}: choose a form column.`)
      }
      if (p.source === 'const' && !(p.text || '')) {
        out.push(`Part ${n}: enter the fixed text.`)
      }
      if (p.source === 'lookup') {
        if (!(p.table || '').trim()) out.push(`Part ${n}: choose a lookup table.`)
        if (!(p.match_column || '').trim()) out.push(`Part ${n}: choose the column to match on.`)
        if (!(p.value_column || '').trim()) out.push(`Part ${n}: choose the value to use.`)
        if (!(p.match_form_column || '').trim()) out.push(`Part ${n}: choose the form field to match against.`)
      }
    })
    if (width < 1) out.push('The number needs at least one digit.')
    return out
  }, [parts, width])

  if (!open || !column) return null

  const patchPart = (i: number, p: Partial<SeriesPart>) => {
    setParts((list) => list.map((it, idx) => (idx === i ? { ...it, ...p } : it)))
  }
  const move = (i: number, delta: number) => {
    setParts((list) => {
      const next = [...list]
      const j = i + delta
      if (j < 0 || j >= next.length) return list
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }
  const remove = (i: number) => setParts((l) => l.filter((_, idx) => idx !== i))
  const add = (source: SeriesPartSource) => {
    setParts((l) => [...l, newPart(source, formColumns[0])])
  }

  const changeSource = (i: number, source: SeriesPartSource) => {
    setParts((list) => list.map((it, idx) => (
      idx === i ? newPart(source, formColumns[0]) : it
    )))
  }

  const save = () => {
    if (problems.length) return
    onSave({
      parts: parts.map((p) => ({ ...p })),
      width,
      ...(separator ? { separator } : {}),
    })
    onClose()
  }

  const colOptions = (table?: string) => colsByTable[(table || '').trim()] || []

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`ID series · ${column.heading || column.name}`}
      size="wide"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={smallBtn}>Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={problems.length > 0}
            style={{
              ...smallBtn, border: 'none',
              background: problems.length ? 'var(--color-border)' : 'var(--color-accent)',
              color: '#fff', fontWeight: 600,
              cursor: problems.length ? 'not-allowed' : 'pointer',
            }}
          >Save</button>
        </div>
      )}
    >
      {/* ── Column budget: what the id may grow to vs what the column holds ── */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        padding: '8px 12px', marginBottom: 14, borderRadius: 6,
        border: `1px solid ${certainOverflow ? 'var(--color-error, #dc2626)'
          : overflows ? '#f59e0b' : 'var(--color-border)'}`,
        background: 'var(--color-bg)', fontSize: '0.8425rem',
      }}>
        <span style={{ fontWeight: 600 }}>
          {column.heading || column.name}
        </span>
        <span style={{ color: 'var(--color-text-secondary)' }}>
          {colType}{colSize ? `(${colSize})` : ''}
          {colSize ? ` — holds ${colSize} characters` : ' — no size set'}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          fontWeight: 600,
          color: certainOverflow ? 'var(--color-error, #dc2626)'
            : overflows ? '#f59e0b' : 'var(--color-text-secondary)',
        }}>
          this id uses {budget.estimated && budget.high !== budget.low
            ? `${budget.low}–${budget.high}` : budget.high}
          {colSize ? ` of ${colSize}` : ''}
        </span>
      </div>
      {colSize > 0 && overflows && (
        <div style={{
          marginTop: -8, marginBottom: 12, fontSize: '0.8125rem',
          color: certainOverflow ? 'var(--color-error, #dc2626)' : '#f59e0b',
        }}>
          {certainOverflow
            ? `The id will not fit — widen ${column.heading || column.name} to at least ${budget.high} characters, or shorten the prefix.`
            : `A part that takes a whole column could push this past ${colSize} characters. Set a length on it, or widen the column.`}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
        {/* ── Parts builder ── */}
        <div>
          <div style={{ ...overlayLbl, marginBottom: 8 }}>
            The id is built from these parts, in order, followed by a number
            that counts up{hasDataPart
              ? <> <strong>separately for each different prefix</strong>.</>
              : <> for every record. Add a form-column part if you want a
                separate counter per country, branch, type, …</>}
          </div>

          {parts.map((p, i) => {
            const source = (p.source || 'form') as SeriesPartSource
            return (
              <div
                key={i}
                style={{
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  padding: '10px 12px', marginBottom: 8,
                  background: 'var(--color-bg)',
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)',
                    minWidth: 44,
                  }}>Part {i + 1}</span>
                  <select
                    style={{ ...input, flex: 1 }}
                    value={source}
                    onChange={(e) => changeSource(i, e.target.value as SeriesPartSource)}
                  >
                    {(Object.keys(SOURCE_LABELS) as SeriesPartSource[]).map((s) => (
                      <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                    ))}
                  </select>
                  <button type="button" style={iconBtn} title="Move up"
                    disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" style={iconBtn} title="Move down"
                    disabled={i === parts.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button
                    type="button"
                    style={{ ...iconBtn, color: 'var(--color-error, #dc2626)' }}
                    title="Remove this part"
                    onClick={() => remove(i)}
                  >×</button>
                </div>

                <div style={{
                  fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8,
                }}>{SOURCE_HINTS[source]}</div>

                {source === 'const' && (
                  <>
                    <label style={overlayLbl}>Text</label>
                    <input
                      style={input}
                      value={p.text || ''}
                      placeholder="ART-"
                      onChange={(e) => patchPart(i, { text: e.target.value })}
                    />
                  </>
                )}

                {source === 'date' && (
                  <>
                    <label style={overlayLbl}>Format</label>
                    <select
                      style={input}
                      value={p.format || 'YYYY'}
                      onChange={(e) => patchPart(i, {
                        format: e.target.value as SeriesDateFormat,
                      })}
                    >
                      {DATE_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <div style={{
                      fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4,
                    }}>
                      Because the prefix changes when the period does, the
                      counter starts again at 1 — no yearly reset to run by hand.
                    </div>
                  </>
                )}

                {source === 'lookup' && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div>
                      <label style={overlayLbl}>Look up in table</label>
                      <input
                        style={input}
                        list={`series-tables-${i}`}
                        value={p.table || ''}
                        placeholder="COUNTRY_MASTER"
                        onChange={(e) => patchPart(i, { table: e.target.value })}
                      />
                      <datalist id={`series-tables-${i}`}>
                        {tables.map((t) => <option key={t} value={t} />)}
                      </datalist>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={overlayLbl}>Match this column…</label>
                        <input
                          style={input}
                          list={`series-mcols-${i}`}
                          value={p.match_column || ''}
                          placeholder="COUNTRY_NAME"
                          onChange={(e) => patchPart(i, { match_column: e.target.value })}
                        />
                        <datalist id={`series-mcols-${i}`}>
                          {colOptions(p.table).map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                      <div>
                        <label style={overlayLbl}>…against this form field</label>
                        <select
                          style={input}
                          value={p.match_form_column || ''}
                          onChange={(e) => patchPart(i, { match_form_column: e.target.value })}
                        >
                          <option value="">— select —</option>
                          {formColumns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={overlayLbl}>Use this value in the id</label>
                      <input
                        style={input}
                        list={`series-vcols-${i}`}
                        value={p.value_column || ''}
                        placeholder="CONTINENT_CODE"
                        onChange={(e) => patchPart(i, { value_column: e.target.value })}
                      />
                      <datalist id={`series-vcols-${i}`}>
                        {colOptions(p.table).map((c) => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                  </div>
                )}

                {source === 'form' && (
                  <>
                    <label style={overlayLbl}>Column</label>
                    <select
                      style={input}
                      value={p.column || ''}
                      onChange={(e) => patchPart(i, { column: e.target.value })}
                    >
                      <option value="">— select column —</option>
                      {formColumns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </>
                )}

                {(source === 'form' || source === 'lookup') && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 8, marginTop: 8,
                  }}>
                    <div>
                      <label style={overlayLbl}>Start at character</label>
                      <input
                        style={input} type="number" min={1}
                        value={p.start ?? 1}
                        onChange={(e) => patchPart(i, { start: Number(e.target.value) || 1 })}
                      />
                    </div>
                    <div>
                      <label style={overlayLbl}>How many</label>
                      <input
                        style={input} type="number" min={0}
                        value={p.length ?? ''}
                        placeholder="all"
                        onChange={(e) => patchPart(i, {
                          length: e.target.value === '' ? undefined : Number(e.target.value),
                        })}
                      />
                    </div>
                    <div>
                      <label style={overlayLbl}>Letter case</label>
                      <select
                        style={input}
                        value={p.transform || 'none'}
                        onChange={(e) => patchPart(i, {
                          transform: e.target.value as SeriesPart['transform'],
                        })}
                      >
                        <option value="none">Leave as typed</option>
                        <option value="upper">UPPERCASE</option>
                        <option value="lower">lowercase</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {(Object.keys(SOURCE_LABELS) as SeriesPartSource[]).map((s) => (
              <button key={s} type="button" style={smallBtn} onClick={() => add(s)}>
                + {SOURCE_LABELS[s]}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={overlayLbl}>Number of digits</label>
              <input
                style={input} type="number" min={1}
                value={width}
                onChange={(e) => setWidth(Math.max(1, Number(e.target.value) || 1))}
              />
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                Zero-padded. If a series ever exceeds it, the number grows a
                digit rather than repeating.
              </div>
            </div>
            <div>
              <label style={overlayLbl}>Separator before the number</label>
              <input
                style={input}
                value={separator}
                placeholder="(none)"
                onChange={(e) => setSeparator(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Live preview ── */}
        <div style={{
          borderLeft: '1px solid var(--color-border)', paddingLeft: 14,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: 0.4, color: 'var(--color-text-secondary)', marginBottom: 8,
          }}>Live preview</div>

          {problems.length ? (
            <div style={{ fontSize: 13, color: 'var(--color-error, #dc2626)' }}>
              {problems.map((p, i) => <div key={i} style={{ marginBottom: 4 }}>• {p}</div>)}
            </div>
          ) : (
            <>
              <div style={{
                padding: '10px 12px', border: '1px solid var(--color-border)',
                borderRadius: 6, background: 'var(--color-bg)',
                fontFamily: 'monospace', fontSize: 14, lineHeight: 1.9,
              }}>
                {hasDataPart
                  // Two different source values side by side — that contrast is
                  // what shows the counters are independent.
                  ? [0, 1].map((variant) => (
                    <div key={variant}>
                      {[1, 2].map((n) => (
                        <div key={n}>{previewId(draft, variant, n)}</div>
                      ))}
                    </div>
                  ))
                  // One declared series: show it simply counting up.
                  : [1, 2, 3].map((n) => (
                    <div key={n}>{previewId(draft, 0, n)}</div>
                  ))}
              </div>
              <div style={{
                fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8,
              }}>
                {hasDataPart
                  ? <>Sample values “{SAMPLE_VALUES[0]}” and “{SAMPLE_VALUES[1]}”
                    — each gets its own counter starting at 1.</>
                  : <>One series for the whole table: every record takes the next
                    number.</>}
              </div>
            </>
          )}

          {hasDataPart && (
            <div style={{
              fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 14,
              borderTop: '1px solid var(--color-border)', paddingTop: 10,
            }}>
              Because the id carries this data, changing a field it is built
              from later is <strong>blocked</strong> — records already issued
              keep the id they were given.
            </div>
          )}
        </div>
      </div>

    </Modal>
  )
}
